import React, { useState, useEffect, useContext, useMemo } from "react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";


const POR_TELEFONO = ["whatsapp", "whatsapp_cloud"];

const IDENTIDAD_PROPIA = ["facebook", "instagram"];

const NUNCA_DESTINO = ["webchat", "sandbox"];

const esTelefono = (valor) => {
  const digitos = String(valor ?? "").replace(/[^0-9]/g, "");
  return digitos.length >= 8 && digitos.length <= 15;
};

const NOMBRE_CANAL = {
  whatsapp: "WhatsApp",
  whatsapp_cloud: "WhatsApp Cloud",
  webchat: "Chat web",
  facebook: "Messenger",
  instagram: "Instagram",
  sandbox: "Sandbox",
};

const CambiarDestinoModal = ({ modalOpen, onClose, ticket, onHecho }) => {
  const [conexion, setConexion] = useState("");
  const [cola, setCola] = useState("");
  const [colas, setColas] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const { findAll: findAllQueues } = useQueues();
  const { whatsApps } = useWhatsApps();
  const { user } = useContext(AuthContext);

  const misColas = useMemo(
    () => (user?.queues || []).map((q) => q.id),
    [user]
  );

  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const lista = await findAllQueues();
        if (vivo) setColas(lista || []);
      } catch (err) {
        if (vivo) setColas([]);
      }
    })();

    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!modalOpen) return;

    setConexion(ticket?.whatsappId ?? "");
    setCola(ticket?.queueId ?? "");
  }, [modalOpen, ticket?.whatsappId, ticket?.queueId]);

  const destino = (whatsApps || []).find(
    (w) => String(w.id) === String(conexion)
  );

  const faltaTelefono =
    destino &&
    POR_TELEFONO.includes(destino.channel) &&
    !esTelefono(ticket?.contact?.number);

  const cambiaConexion =
    conexion && String(conexion) !== String(ticket?.whatsappId ?? "");
  const cambiaCola = String(cola ?? "") !== String(ticket?.queueId ?? "");
  const hayCambios = cambiaConexion || cambiaCola;

  const guardar = async () => {
    if (!ticket?.id || !hayCambios) return;

    setGuardando(true);

    try {
      const datos = {};

      if (cambiaConexion) datos.whatsappId = conexion;
      if (cambiaCola) datos.queueId = cola === "" ? null : cola;

      await api.put(`/tickets/${ticket.id}`, datos);

      if (typeof onHecho === "function") onHecho();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setGuardando(false);
    }
  };

  const actualId = String(ticket?.whatsappId ?? "");
  const canalActual = ticket?.channel;
  const tieneTelefono = esTelefono(ticket?.contact?.number);

  const sirve = (w) => {
    if (String(w.id) === actualId) return true;

    if (w.status !== "CONNECTED") return false;
    if (NUNCA_DESTINO.includes(w.channel)) return false;

    if (misColas.length > 0) {
      const colasDeLaConexion = (w.queues || []).map((q) => q.id);

      if (!colasDeLaConexion.some((id) => misColas.includes(id))) return false;
    }

    if (IDENTIDAD_PROPIA.includes(w.channel)) return false;

    if (POR_TELEFONO.includes(w.channel)) {
      if (IDENTIDAD_PROPIA.includes(canalActual)) return false;
      return tieneTelefono;
    }

    return false;
  };

  const disponibles = (whatsApps || []).filter(sirve);

  const porCanal = disponibles.reduce((acc, w) => {
    const k = w.channel || "otro";
    (acc[k] = acc[k] || []).push(w);
    return acc;
  }, {});

  const motivoListaCorta = () => {
    if (IDENTIDAD_PROPIA.includes(canalActual)) {
      return (
        "Esta conversación no se puede mover: en Messenger e Instagram el cliente se identifica con un código propio de esa página, que no sirve en ningún otro canal."
      );
    }

    if (!tieneTelefono) {
      return (
        "Este contacto no tiene teléfono, así que no se puede mover a WhatsApp. Añádelo en su ficha y vuelve a intentarlo."
      );
    }

    if (misColas.length > 0) {
      return (
        "No hay otra conexión de tus departamentos a la que mover esta conversación. Si necesitas otra línea, pídele a un supervisor que te añada a su departamento."
      );
    }

    return "No hay otra conexión conectada a la que mover esta conversación.";
  };

  const etiqueta = (w) =>
    `${w.name} — ${NOMBRE_CANAL[w.channel] || w.channel}` +
    (String(w.id) === String(ticket?.whatsappId ?? "") ? " (actual)" : "");

  return (
    <Dialog open={modalOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cambiar conexión y departamento</DialogTitle>

      <DialogContent dividers>
        <DialogContentText style={{ marginBottom: 18, fontSize: 13.5 }}>
          Sigues tú con {ticket?.contact?.name || "este cliente"}. Solo cambia
          por dónde le respondes.
        </DialogContentText>

        {}
        <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
          <InputLabel>Conexión</InputLabel>
          <Select
            value={conexion}
            onChange={(e) => setConexion(e.target.value)}
            label="Conexión"
          >
            {Object.keys(porCanal).flatMap((canal) => [
              <ListSubheader key={"g-" + canal} disableSticky>
                {NOMBRE_CANAL[canal] || canal}
              </ListSubheader>,
              ...porCanal[canal].map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                  {String(w.id) === actualId ? " (actual)" : ""}
                </MenuItem>
              ))
            ])}
          </Select>
        </FormControl>

        {}
        {}
        {disponibles.length <= 1 && (
          <div
            style={{
              marginTop: -8,
              marginBottom: 16,
              fontSize: 12.5,
              opacity: 0.75,
              lineHeight: 1.4,
            }}
          >
            {motivoListaCorta()}
          </div>
        )}

        <FormControl variant="outlined" fullWidth>
          <InputLabel>Departamento</InputLabel>
          <Select
            value={cola}
            onChange={(e) => setCola(e.target.value)}
            label="Departamento"
          >
            <MenuItem value="">
              <em>Sin departamento</em>
            </MenuItem>
            {colas.map((q) => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
                {String(q.id) === String(ticket?.queueId ?? "") ? " (actual)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {faltaTelefono && (
          <div
            style={{
              marginTop: 16,
              padding: "9px 12px",
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.4,
              backgroundColor: "#fdf3e3",
              color: "#7a6023",
              border: "1px solid #e3cfa0",
            }}
          >
            Este contacto no dejó teléfono, así que por{" "}
            {NOMBRE_CANAL[destino.channel] || destino.channel} no hay a dónde
            escribirle. Pídeselo antes, o añádelo en su ficha.
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={guardando}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          variant="contained"
          color="primary"
          onClick={guardar}
          loading={guardando}
          disabled={!hayCambios}
        >
          Guardar
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default CambiarDestinoModal;
